import type { Card } from './types';
import type { DeckManager } from './DeckManager';

export class UIManager {
  private deckManager: DeckManager;
  private cards: Map<number, Card>;
  // For double-faced cards, the deck identity (base) can differ from the face currently displayed.
  private currentCardBase: Card | null = null;
  private currentCardShown: Card | null = null;
  private showBackFace: boolean = false; // FIX #3: État flip

  constructor(deckManager: DeckManager, cards: Card[]) {
    this.deckManager = deckManager;
    this.cards = new Map(cards.map(c => [c.number, c]));
    this.init();
  }

  private init(): void {
    
    this.initDeckPanelToggle();
// Clear button
    const btnClear = document.getElementById('btnClear');
    btnClear?.addEventListener('click', () => {
      if (confirm('Vider le deck ?')) {
        this.deckManager.clear();
      }
    });

    // Export button
    const btnExport = document.getElementById('btnExport');
    btnExport?.addEventListener('click', () => {
      const text = this.deckManager.exportText();
      this.copyToClipboard(text);
      this.showToast('Deck copié dans le presse-papier !');
    });

    // Import button
    const btnImport = document.getElementById('btnImport');
    btnImport?.addEventListener('click', () => {
      this.openImportModal();
    });

    // Deck name input
    const deckNameInput = document.getElementById('deckNameInput') as HTMLInputElement;
    if (deckNameInput) {
      // Charger le nom initial
      deckNameInput.value = this.deckManager.getDeckName();
      
      // Sauvegarder quand on modifie le nom
      deckNameInput.addEventListener('input', () => {
        this.deckManager.setDeckName(deckNameInput.value);
      });
      
      deckNameInput.addEventListener('blur', () => {
        this.deckManager.setDeckName(deckNameInput.value);
      });
    }

    // Import modal controls
    const importModalClose = document.getElementById('importModalClose');
    const importModalBackdrop = document.getElementById('importModalBackdrop');
    const importCancelBtn = document.getElementById('importCancelBtn');
    const importConfirmBtn = document.getElementById('importConfirmBtn');

    importModalClose?.addEventListener('click', () => this.closeImportModal());
    importModalBackdrop?.addEventListener('click', () => this.closeImportModal());
    importCancelBtn?.addEventListener('click', () => this.closeImportModal());
    importConfirmBtn?.addEventListener('click', () => this.confirmImport());


    // Modal close
    const modalClose = document.getElementById('modalClose');
    const modalBackdrop = document.getElementById('modalBackdrop');
    
    modalClose?.addEventListener('click', () => this.closeModal());
    modalBackdrop?.addEventListener('click', () => this.closeModal());

    // Modal actions
    const modalAddBtn = document.getElementById('modalAddBtn');
    const modalRemoveBtn = document.getElementById('modalRemoveBtn');

    modalAddBtn?.addEventListener('click', () => {
      if (this.currentCardBase) {
        if (this.deckManager.addCard(this.currentCardBase.number)) {
          this.showToast('Carte ajoutée !');
          this.updateModalDeckBadge(); // Mettre à jour l'indicateur du modal
        } else {
          const rarity = this.currentCardBase.rarity?.toUpperCase() || 'C';
          const max = rarity === 'R' ? 2 : 4;
          this.showToast(`Maximum ${max} copies atteint`);
        }
      }
    });

    modalRemoveBtn?.addEventListener('click', () => {
      if (this.currentCardBase) {
        this.deckManager.removeCard(this.currentCardBase.number);
        this.showToast('Carte retirée');
        this.updateModalDeckBadge(); // Mettre à jour l'indicateur du modal
      }
    });

    // Escape key to close modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeModal();
      }
    });
  }

  updateDeckDisplay(): void {
    const deckList = document.getElementById('deckList');
    if (!deckList) {
      return;
    }

    const deck = this.deckManager.getDeck();
    
    if (deck.length === 0) {
      deckList.innerHTML = `
        <div style="text-align: center; padding: 40px 20px; color: rgba(255,255,255,0.5); font-size: 13px;">
          Cliquez sur les cartes pour les ajouter à votre deck
        </div>
      `;
    } else {
      deckList.innerHTML = deck
        .sort((a, b) => a.number - b.number)
        .map(entry => {
          const card = this.cards.get(entry.number);
          if (!card) return '';

          return `
            <div class="deck-item" data-card="${card.number}">
              <div class="deck-item-qty">${entry.qty}x</div>
              <div class="deck-item-info">
                <div class="deck-item-name">${String(card.number).padStart(3, '0')} - ${card.name}</div>
                <div class="deck-item-meta">${card.rarity} • ${card.points}★ • Coût ${card.cost}</div>
              </div>
              <div class="deck-item-actions">
                <button class="btn-sm" data-action="add" data-card="${card.number}">+</button>
                <button class="btn-sm" data-action="remove" data-card="${card.number}">−</button>
              </div>
            </div>
          `;
        })
        .join('');

      // Add event listeners
      deckList.querySelectorAll('[data-action="add"]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const cardNum = parseInt((btn as HTMLElement).dataset.card || '0');
          if (this.deckManager.addCard(cardNum)) {
            this.showToast('Carte ajoutée !');
          } else {
            const card = this.cards.get(cardNum);
            const rarity = card?.rarity?.toUpperCase() || 'C';
            const max = rarity === 'R' ? 2 : 4;
            this.showToast(`Maximum ${max} copies atteint`);
          }
        });
      });

      deckList.querySelectorAll('[data-action="remove"]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const cardNum = parseInt((btn as HTMLElement).dataset.card || '0');
          this.deckManager.removeCard(cardNum);
        });
      });

      // Click on deck item to view card
      const items = deckList.querySelectorAll('.deck-item');
      
      items.forEach(item => {
        const cardNum = parseInt((item as HTMLElement).dataset.card || '0');
        const card = this.cards.get(cardNum);
        
        // Click on info section (name + meta)
        const infoEl = item.querySelector('.deck-item-info');
        if (infoEl && card) {
          (infoEl as HTMLElement).style.cursor = 'pointer';
          infoEl.addEventListener('click', (e) => {
            e.stopPropagation();
            this.showCardModal(card);
          });
        }
        
        // Click on qty section
        const qtyEl = item.querySelector('.deck-item-qty');
        if (qtyEl && card) {
          (qtyEl as HTMLElement).style.cursor = 'pointer';
          qtyEl.addEventListener('click', (e) => {
            e.stopPropagation();
            this.showCardModal(card);
          });
        }
      });
    }

    // Update stats
    this.updateStats();
  }

  private updateStats(): void {
    const total = this.deckManager.getTotalCards();
    const rares = this.deckManager.getTotalRares();
    const { legal } = this.deckManager.isLegal();


    const deckSize = document.getElementById('deckSize');
    const deckRares = document.getElementById('deckRares');
    const deckLegality = document.getElementById('deckLegality');

    if (deckSize) {
      deckSize.textContent = `${total}/40`;
      deckSize.className = total === 40 ? 'stat-value valid' : 'stat-value invalid';
    } else {
    }

    if (deckRares) {
      deckRares.textContent = `${rares}/12`;
      deckRares.className = rares <= 12 ? 'stat-value valid' : 'stat-value invalid';
    } else {
    }

    if (deckLegality) {
      deckLegality.textContent = legal ? '✓ Légal' : '✗ Illégal';
      deckLegality.className = legal ? 'stat-value valid' : 'stat-value invalid';
    } else {
    }
  }

  // base = identity for deck operations, shown = face to display (can differ for double-faced cards)
  showCardModal(base: Card, shown: Card = base): void {
    
    // FIX #3: Calculer face alternative (ne reset que si nouvelle carte)
    const isNewCard = !this.currentCardBase || this.currentCardBase.number !== base.number;
    if (isNewCard) this.showBackFace = false;
    
    const hasFlip = !!(shown.transformTo || shown.transformFrom);
    const otherFaceId = shown.transformTo || shown.transformFrom;
    const otherCard = (hasFlip && otherFaceId) ? this.cards.get(otherFaceId) : null;
    const displayCard = (this.showBackFace && otherCard) ? otherCard : shown;
    
    this.currentCardBase = base;
    this.currentCardShown = shown;

    const modal = document.getElementById('cardModal');
    const backdrop = document.getElementById('modalBackdrop');
    
    
    const modalCardImage = document.getElementById('modalCardImage') as HTMLImageElement;
    const modalCardName = document.getElementById('modalCardName');
    const modalCardMeta = document.getElementById('modalCardMeta');
    const modalCardText = document.getElementById('modalCardText');

    if (modalCardImage) {
      modalCardImage.src = displayCard.image || '';
      modalCardImage.alt = displayCard.name;
    }

    if (modalCardName) {
      // Supprimer ancien bouton flip s'il existe
      const existingFlip = modalCardName.parentElement?.querySelector('.flip-btn');
      if (existingFlip) existingFlip.remove();
      
      modalCardName.textContent = `${String(displayCard.number).padStart(3, '0')} - ${displayCard.name}`;
      
      // FIX #3: Ajouter bouton flip si carte a transformTo/From
      if (hasFlip && otherCard && modalCardName.parentElement) {
        const flipBtn = document.createElement('button');
        flipBtn.className = 'flip-btn' + (this.showBackFace ? ' is-back' : '');
        flipBtn.innerHTML = '↻';
        flipBtn.onclick = (e) => {
          e.stopPropagation();
          this.showBackFace = !this.showBackFace;
          this.showCardModal(base, shown);
        };
        modalCardName.parentElement.appendChild(flipBtn);
      }
    }

    if (modalCardMeta) {
      modalCardMeta.innerHTML = `
        <span class="modal-badge ${displayCard.rarity === 'R' ? 'rare' : 'common'}">${displayCard.rarity}</span>
        <span class="modal-badge">Coût ${displayCard.cost}</span>
        <span class="modal-badge">${displayCard.points}★</span>
        <span class="modal-badge">${displayCard.type}</span>
        <span class="modal-badge" style="background: rgba(102, 126, 234, 0.3);">Deck: ${this.deckManager.getCardQuantity(base.number)}/${(base.rarity?.toUpperCase() || 'C') === 'R' ? 2 : 4}</span>
      `;
    }

    if (modalCardText) {
      modalCardText.textContent = displayCard.text || 'Aucun effet spécial';
    }

    modal?.classList.add('active');
    backdrop?.classList.add('active');
  }

  closeModal(): void {
    const modal = document.getElementById('cardModal');
    const backdrop = document.getElementById('modalBackdrop');
    
    modal?.classList.remove('active');
    backdrop?.classList.remove('active');
    
    this.currentCardBase = null;
    this.currentCardShown = null;
  }

  // Met à jour l'indicateur "Deck: X/Y" dans le modal de carte
  private updateModalDeckBadge(): void {
    if (!this.currentCardBase) return;
    
    const modalCardMeta = document.getElementById('modalCardMeta');
    if (!modalCardMeta) return;
    
    const qty = this.deckManager.getCardQuantity(this.currentCardBase.number);
    const rarity = (this.currentCardBase.rarity?.toUpperCase() || 'C');
    const max = rarity === 'R' ? 2 : 4;
    
    // Trouver le badge "Deck: X/Y" et le mettre à jour
    const badges = modalCardMeta.querySelectorAll('.modal-badge');
    badges.forEach(badge => {
      if (badge.textContent?.startsWith('Deck:')) {
        badge.textContent = `Deck: ${qty}/${max}`;
      }
    });
  }

  private copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).catch(err => {
      console.error('Failed to copy:', err);
    });
  }

  private showToast(message: string): void {
    // Create toast element
    const toast = document.createElement('div');
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 30px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 12px 24px;
      border-radius: 25px;
      font-size: 14px;
      font-weight: 600;
      z-index: 10000;
      pointer-events: none;
      animation: slideUp 0.3s ease;
      border: 1px solid rgba(255, 255, 255, 0.2);
      backdrop-filter: blur(10px);
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.animation = 'slideDown 0.3s ease';
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 300);
    }, 2000);
  }

  hideLoading(): void {
    const loading = document.getElementById('loading');
    if (loading) {
      loading.style.opacity = '0';
      setTimeout(() => {
        loading.style.display = 'none';
      }, 300);
    }
  }

  private initDeckPanelToggle(): void {
    const deckPanel = document.getElementById('deckPanel') as HTMLElement | null;
    const deckHeader = document.getElementById('deckHeader') as HTMLElement | null;
    const deckToggle = document.getElementById('deckToggle') as HTMLButtonElement | null;
    if (!deckPanel || !deckHeader || !deckToggle) return;

    const mq = window.matchMedia('(max-width: 768px)');

    const applyMode = () => {
      if (mq.matches) {
        // Mobile: start collapsed to avoid blocking the card grid
        if (!deckPanel.classList.contains('expanded')) {
          deckPanel.classList.add('collapsed');
        }
      } else {
        // Desktop: keep current state (don't force expanded)
        // Only initialize if no state is set
        if (!deckPanel.classList.contains('collapsed') && !deckPanel.classList.contains('expanded')) {
          deckPanel.classList.remove('collapsed');
        }
      }
    };

    const toggle = () => {
      const isCollapsed = deckPanel.classList.contains('collapsed');
      
      if (isCollapsed) {
        deckPanel.classList.remove('collapsed');
        deckPanel.classList.add('expanded');
      } else {
        deckPanel.classList.add('collapsed');
        deckPanel.classList.remove('expanded');
      }
    };

    deckToggle.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      toggle();
    });

    deckHeader.addEventListener('click', (e) => {
      // On mobile, tapping the header toggles the panel
      if (!mq.matches) return;
      
      // Ne pas toggle si on clique sur l'input du nom
      const target = e.target as HTMLElement;
      if (target.id === 'deckNameInput' || target.closest('#deckNameInput')) {
        e.stopPropagation();
        return;
      }
      
      // avoid toggling when clicking interactive controls inside header (if any later)
      toggle();
    });

    // React to viewport changes
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', applyMode);
    } else {
      // Safari fallback
      // @ts-expect-error legacy
      mq.addListener(applyMode);
    }

    applyMode();
  }

  private openImportModal(): void {
    const modal = document.getElementById('importModal');
    const backdrop = document.getElementById('importModalBackdrop');
    const textarea = document.getElementById('importTextarea') as HTMLTextAreaElement;
    
    if (modal && backdrop) {
      modal.style.display = 'block';
      backdrop.style.display = 'block';
      
      if (textarea) {
        textarea.value = '';
        textarea.focus();
      }
    }
  }

  private closeImportModal(): void {
    const modal = document.getElementById('importModal');
    const backdrop = document.getElementById('importModalBackdrop');
    
    if (modal && backdrop) {
      modal.style.display = 'none';
      backdrop.style.display = 'none';
    }
  }

  private confirmImport(): void {
    const textarea = document.getElementById('importTextarea') as HTMLTextAreaElement;
    
    if (!textarea || !textarea.value.trim()) {
      this.showToast('Decklist vide');
      return;
    }

    const result = this.deckManager.importFromText(textarea.value);
    
    if (result.success) {
      this.showToast(result.message);
      this.closeImportModal();
      
      // Mettre à jour le nom affiché
      const deckNameInput = document.getElementById('deckNameInput') as HTMLInputElement;
      if (deckNameInput) {
        deckNameInput.value = this.deckManager.getDeckName();
      }
    } else {
      this.showToast('❌ ' + result.message);
    }
  }

}
